import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/** Back-office side of client typing: the recorded analyses and the client clusters. */

export interface ClientTypingRunProfile {
    label: string;
    description?: string;
    defining: string[];
    count: number;
    pct: number;
    searchPrompt?: string;
    /** Only on the detail read — the list endpoint strips them. */
    memberIds?: string[];
}

/** One recorded analysis, as the list endpoint returns it (no member ids, no distributions). */
export interface ClientTypingRun {
    _id: string;
    userId: string;
    userEmail: string;
    userName?: string;
    establishmentNames: string[];
    establishmentCount: number;
    summary?: string;
    profiles: ClientTypingRunProfile[];
    profileCount: number;
    unassignedCount: number;
    model?: string;
    aiError?: string;
    durationMs?: number;
    createdAt: string;
}

export interface ClientTypingRunEstablishment {
    establishmentId: string;
    name: string;
    city: string | null;
    categories: string[];
    subCategories: string[];
    deleted?: boolean;
}

export interface ClientTypingRunDetail extends ClientTypingRun {
    establishments: ClientTypingRunEstablishment[];
    statsDigest?: {
        blocks: { id: string; label: string; coverage: number; items: { label: string; count: number; pct: number }[] }[];
        highlights: { label: string; value: string; coverage: number }[];
    };
}

/** An establishment seen across runs — who looked at it, how often. */
export interface AnalyzedEstablishment {
    establishmentId: string;
    name: string;
    city?: string | null;
    categories: string[];
    subCategories: string[];
    runs: number;
    users: string[];
    lastAnalyzedAt: string;
    clusters: { _id: string; name: string }[];
}

export interface ClientClusterMember {
    establishmentId: string;
    name: string;
    city: string | null;
    categories: string[];
    subCategories: string[];
    runs: number;
    lastAnalyzedAt: string | null;
}

export interface ClientCluster {
    _id: string;
    name: string;
    note: string;
    origin: string;
    members: ClientClusterMember[];
    memberCount: number;
    runs: number;
    users: number;
    lastAnalyzedAt: string | null;
    createdAt: string;
}

export interface ClientTypingStats {
    runs: number;
    failed: number;
    establishments: number;
    distinctEstablishments: number;
    profiles: number;
    users: number;
    avgSelection: number;
    avgDurationMs: number;
}

export interface ClientTypingUserStat {
    email: string;
    runs: number;
    establishments: number;
    lastRunAt: string;
}

/** Shared by every read: the same window and filters, so the page's numbers agree. */
export interface ClientTypingQuery {
    days?: number | "all";
    email?: string;
    status?: "ok" | "error" | "all";
    q?: string;
}

const toParams = (query: ClientTypingQuery = {}) => {
    const params = new URLSearchParams();
    if (query.days && query.days !== "all") params.set("days", String(query.days));
    if (query.email) params.set("email", query.email);
    if (query.status && query.status !== "all") params.set("status", query.status);
    if (query.q) params.set("q", query.q);
    return params;
};

export const fetchClientTypingRuns = async (query: ClientTypingQuery, page = 1, limit = 50) => {
    const params = toParams(query);
    params.set("page", String(page));
    params.set("limit", String(limit));
    const { data } = await axios.get(`${API_URL}/admin/client-typings?${params}`, { headers: authHeaders() });
    return data as { runs: ClientTypingRun[]; total: number; page: number; limit: number };
};

export const fetchClientTypingRun = async (id: string) => {
    const { data } = await axios.get(`${API_URL}/admin/client-typings/${id}`, { headers: authHeaders() });
    return data.run as ClientTypingRunDetail;
};

export const fetchClientTypingStats = async (query: ClientTypingQuery) => {
    const { data } = await axios.get(`${API_URL}/admin/client-typings/stats?${toParams(query)}`, {
        headers: authHeaders(),
    });
    return data as { stats: ClientTypingStats; byUser: ClientTypingUserStat[] };
};

export const fetchAnalyzedEstablishments = async (query: ClientTypingQuery, limit = 100) => {
    const params = toParams(query);
    params.set("limit", String(limit));
    const { data } = await axios.get(`${API_URL}/admin/client-typings/establishments?${params}`, {
        headers: authHeaders(),
    });
    return data.establishments as AnalyzedEstablishment[];
};

export const fetchClientClusters = async (query: ClientTypingQuery) => {
    const { data } = await axios.get(`${API_URL}/admin/client-typings/clusters?${toParams(query)}`, {
        headers: authHeaders(),
    });
    return data.clusters as ClientCluster[];
};

export const createClientCluster = async (body: {
    name: string;
    establishmentIds: string[];
    note?: string;
    origin?: string;
}) => {
    const { data } = await axios.post(`${API_URL}/admin/client-typings/clusters`, body, { headers: authHeaders() });
    return data.cluster;
};

export const updateClientCluster = async (
    id: string,
    body: { name?: string; note?: string; addEstablishments?: string[]; removeEstablishments?: string[] }
) => {
    const { data } = await axios.patch(`${API_URL}/admin/client-typings/clusters/${id}`, body, {
        headers: authHeaders(),
    });
    return data.cluster;
};

export const deleteClientCluster = async (id: string) => {
    await axios.delete(`${API_URL}/admin/client-typings/clusters/${id}`, { headers: authHeaders() });
};
